// src/components/Navigation.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiMenu, FiX } from 'react-icons/fi';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  width: 100%;
  background: ${props => props.scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.9)'};
  backdrop-filter: blur(20px);
  z-index: 1000;
  transition: all 0.3s ease;
  border-bottom: 1px solid ${props => props.scrolled ? props.theme.colors.lightGray : 'transparent'};
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 90px;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 20px;
    height: 80px;
  }
`;

const Logo = styled(Link)`
  font-family: ${props => props.theme.fonts.primary};
  font-size: 2rem;
  font-weight: 600;
  color: ${props => props.theme.colors.secondary};
  text-decoration: none;
  letter-spacing: -0.5px;
`;

const NavMenu = styled.ul`
  display: flex;
  list-style: none;
  gap: 3rem;
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    position: fixed;
    top: 80px;
    left: ${props => props.isOpen ? '0' : '-100%'};
    width: 100%;
    height: calc(100vh - 80px);
    background: white;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding-top: 3rem;
    transition: left 0.3s ease;
    box-shadow: ${props => props.theme.shadows.large};
  }
`;

const NavItem = styled.li`
  a {
    color: ${props => props.theme.colors.secondary};
    font-weight: 500;
    font-size: 1rem;
    text-decoration: none;
    padding: 1rem 0;
    position: relative;
    transition: all 0.3s ease;
    
    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 0;
      height: 2px;
      background: ${props => props.theme.colors.primary};
      transition: width 0.3s ease;
    }
    
    &.active,
    &:hover {
      color: ${props => props.theme.colors.primary};
      
      &:after {
        width: 100%;
      }
    }
  }
`;

const MenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${props => props.theme.colors.secondary};
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: block;
  }
`;

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <Nav scrolled={scrolled}>
      <NavContainer>
        <Logo to="/">Apartamenty przy Parku Czartoryskich</Logo>
        
        <NavMenu isOpen={isOpen}>
          <NavItem>
            <Link to="/" className={location.pathname === '/' ? 'active' : ''}>
              Strona główna
            </Link>
          </NavItem>
          <NavItem>
            <Link 
              to="/apartment/1" 
              className={location.pathname === '/apartment/1' ? 'active' : ''}
            >
              Apartament Słoneczny
            </Link>
          </NavItem>
          <NavItem>
            <Link 
              to="/apartment/2" 
              className={location.pathname === '/apartment/2' ? 'active' : ''}
            >
              Apartament Kameralny
            </Link>
          </NavItem>
          <NavItem>
            <Link 
              to="/contact" 
              className={location.pathname === '/contact' ? 'active' : ''}
            >
              Kontakt
            </Link>
          </NavItem>
        </NavMenu>
        
        <MenuToggle onClick={toggleMenu}>
          {isOpen ? <FiX /> : <FiMenu />}
        </MenuToggle>
      </NavContainer>
    </Nav>
  );
};

export default Navigation;